import { api } from "@/api"
import { useQuery } from "@tanstack/vue-query"
import type { MaybeRef } from "@vueuse/core"

type ListMerchantGroupUsersQuery = {
  search?: string
  ordering?:
    | "email"
    | "-email"
    | "name"
    | "-name"
    | "lastLogin"
    | "-lastLogin"
    | "createdAt"
    | "-createdAt"
  schemaName: string
} & Pagination

export type ListMerchantGroupUsersFilter = Pick<
  ListMerchantGroupUsersQuery,
  "search"
>

export type ListMerchantGroupUsersOrdering =
  ListMerchantGroupUsersQuery["ordering"]

export type ListMerchantGroupUsersResponseItem = {
  id: string
  email: string
  firstName: string
  lastName: string
  lastLogin: Date | null
  createdAt: Date
  merchantName: string
  isActive: boolean
}

const listMerchantGroupUsers = async (
  query: ListMerchantGroupUsersQuery
): Promise<
  Paginate<Array<ListMerchantGroupUsersResponseItem>>
> => {
  const { body } = await api.get<
    Paginate<
      RecursivelyConvertDatesToStrings<
        Array<ListMerchantGroupUsersResponseItem>
      >
    >
  >("/merchant-group/users/", {
    query: query,
    isMerchantGroup: true,
  })

  return {
    ...body,
    items: body.items.map((item) => ({
      ...item,
      lastLogin: item.lastLogin
        ? DateFns.parseISO(item.lastLogin)
        : null,
      createdAt: DateFns.parseISO(item.createdAt),
    })),
  }
}

type UseMerchantGroupUsersQueryKey = Omit<
  ListMerchantGroupUsersQuery,
  "schemaName"
> & {
  schemaName: string | undefined
}

export const useMerchantGroupUsers = (
  query: MaybeRef<UseMerchantGroupUsersQueryKey>
) =>
  useQuery({
    queryKey: [
      BASE_MERCHANT_GROUP_USERS_QUERY_KEY,
      query,
    ] as const,
    queryFn: ({ queryKey: [_, query_] }) =>
      listMerchantGroupUsers(
        unref(query_) as ListMerchantGroupUsersQuery
      ),
  })
